class CollectionFilters {
  constructor() {
    if (window.collectionFiltersInstance) {
      return window.collectionFiltersInstance;
    }

    this.drawer = document.getElementById('CollectionFiltersDrawer');
    this.overlay = document.querySelector('.collection-filters-overlay');
    this.filterForm = document.getElementById('collection-filters');
    this.productGrid = document.getElementById('product-grid');
    this.sortSelector = document.getElementById('sort-by');
    this.filterTriggerButton = document.querySelector('.collection-filters-nav__trigger');
    this.ajaxinate = null;
    this.isInitialized = false;

    window.collectionFiltersInstance = this;
    this.init();
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.bindEvents();
    this.initAjaxinate();
    this.setupFilterCountListeners();
    this.updateActiveFilterGroupsCount();
    this.initPriceRangeFromURL();
    this.isInitialized = true;
  }

  bindEvents() {
    // Drawer open/close handlers
    document.querySelectorAll('[data-action="open-drawer"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.openDrawer();
      });
    });

    document.querySelectorAll('[data-action="close-drawer"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDrawer();
      });
    });

    document.querySelectorAll('[data-action="clear-filters"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.handleClearFilters(event);
      });
    });

    // Filter form submission
    if (this.filterForm) {
      this.filterForm.addEventListener('submit', this.handleSubmit.bind(this));
      
      // Add clear filters handler with stopPropagation
      const clearButton = this.filterForm.querySelector('[data-action="clear-filters"]');
      if (clearButton) {
        clearButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.handleClearFilters(event);
        });
      }
    }

    // Close drawer on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    // Add sort change handler
    if (this.sortSelector) {
      this.sortSelector.addEventListener('change', this.handleSort.bind(this));
    }
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  initAjaxinate() {
    // Always destroy existing instance first
    if (this.ajaxinate) {
      this.ajaxinate.destroy();
      this.ajaxinate = null;
    }

    // Remove any existing event listeners
    document.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('resize', this.checkIfPaginationInView);
    window.removeEventListener('orientationchange', this.checkIfPaginationInView);

    // Only initialize if we have both container and pagination elements
    const container = document.querySelector('#AjaxinateLoop');
    const pagination = document.querySelector('#AjaxinatePagination');
    
    if (!container || !pagination) {
      return;
    }

    this.ajaxinate = new Ajaxinate({
      container: '#AjaxinateLoop',
      pagination: '#AjaxinatePagination',
      loadingText: 'Loading more products...',
      callback: () => {
        // Optional: Add any callback functionality after new products are loaded
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const formData = new FormData(this.filterForm);
    const searchParams = new URLSearchParams(formData);

    // Retain the sort parameter if it exists
    const currentParams = new URLSearchParams(window.location.search);
    const sortParam = currentParams.get('sort_by');
    if (sortParam) {
      searchParams.set('sort_by', sortParam);
    }

    const url = `${window.location.pathname}?${searchParams.toString()}`;

    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update URL and reinitialize price range to match new URL
      window.history.pushState({ path: url }, '', url);
      this.initPriceRangeFromURL();
      
      // Update the filter count after URL is updated
      this.updateActiveFilterGroupsCount();
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
      
      // Close drawer after applying filters
      this.closeDrawer();
      
    } catch (error) {
      console.error('Error updating filters:', error);
    }
  }

  async handleClearFilters(event) {
    event.preventDefault();
    
    const classes = event.target.classList
    const isDrawerButton = classes.contains('reset-button')

    // Clear all checkboxes
    this.filterForm.querySelectorAll('input[type="checkbox"]').forEach(input => {
      input.checked = false;
    });

    // Reset all filter counts
    document.querySelectorAll('[data-filter-count]').forEach(countSpan => {
      countSpan.textContent = '';
    });

    const url = `${window.location.pathname}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update URL and reinitialize price range to match new URL
      window.history.pushState({ path: url }, '', url);
      this.initPriceRangeFromURL();
      
      // Update the filter groups count AFTER URL is cleared
      this.updateActiveFilterGroupsCount();
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
      
      // Close drawer
      if (!isDrawerButton && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
      
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
  }

  async handleSort(event) {
    const selectedValue = event.target.value;
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('sort_by', selectedValue);
    const url = `${window.location.pathname}?${currentParams.toString()}`;

    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }
      
      window.history.pushState({ path: url }, '', url);
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
    } catch (error) {
      console.error('Error updating sort:', error);
    }
  }

  setupFilterCountListeners() {
    const filterGroups = document.querySelectorAll('.filter-group');

    filterGroups.forEach(group => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      const countSpan = group.querySelector('[data-filter-count]');

      if (!checkboxes.length || !countSpan) return;

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length;
          countSpan.textContent = checkedCount > 0 ? `(${checkedCount})` : '';
          this.updateActiveFilterGroupsCount();
        });
      });
    });
  }

  updateActiveFilterGroupsCount() {
    if (!this.filterTriggerButton) return;

    // Count active checkbox groups
    const activeGroups = Array.from(document.querySelectorAll('.filter-group')).filter(group => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]:checked');
      return checkboxes.length > 0;
    }).length;

    // Check if price filter is active
    const urlParams = new URLSearchParams(window.location.search);
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');
    const isPriceFilterActive = minPrice || maxPrice;

    // Add price filter to total count if active
    const totalActiveFilters = activeGroups + (isPriceFilterActive ? 1 : 0);

    this.filterTriggerButton.textContent = `Filter${totalActiveFilters > 0 ? `s (${totalActiveFilters})` : ''} +`;
  }

  initPriceRangeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');

    this.filterForm.querySelectorAll('.price-range-slider').forEach(slider => {
      const minInput = slider.querySelector('.price-range-min-input');
      const maxInput = slider.querySelector('.price-range-max-input');
      const minHidden = slider.querySelector('[data-price-range-min]');
      const maxHidden = slider.querySelector('[data-price-range-max]');
      const minLabel = slider.querySelector('.price-range-min-label');
      const maxLabel = slider.querySelector('.price-range-max-label');
      
      // Set values based on URL params or defaults
      minInput.value = minPrice || minInput.min;
      maxInput.value = maxPrice || maxInput.max;
      
      if (minHidden) minHidden.value = minInput.value;
      if (maxHidden) maxHidden.value = maxInput.value;
      
      // Update labels
      minLabel.textContent = formatMoney(Math.round(minInput.value * 100));
      maxLabel.textContent = formatMoney(Math.round(maxInput.value * 100));
      
      // Trigger input events to update the visual state
      minInput.dispatchEvent(new Event('input'));
      maxInput.dispatchEvent(new Event('input'));
    });
  }
}

// Single initialization point
document.addEventListener('DOMContentLoaded', () => {
  window.collectionFiltersInstance = new CollectionFilters();
});

function formatMoney(cents) {
  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents);
  }
  return '$' + (cents/100).toFixed(2);
}